import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { Copy, Pencil, Plus, Trash2 } from "lucide-react";

import {
  deleteInstance,
  deleteSection,
  listGatewayUsage,
  listGateways,
  type InstanceRef,
} from "@/api/catalog";
import { listComponents } from "@/api/components";
import { CatalogHealthBadge } from "@/components/CatalogHealthBadge";
import { ConfirmModal } from "@/components/ConfirmModal";
import { DeleteError } from "@/components/DeleteError";
import { MenuDivider, MenuItem } from "@/components/KebabMenu";
import { RefreshFailed, loadFailure } from "@/components/Spinner";
import { ButtonLink, EmptyState, Page, PageHeader, Table, type TableColumn } from "@/components/ui";
import { CATALOG } from "@/lib/sections";
import { formatDateTime } from "@/lib/dates";
import type { GatewayCatalogItem, GatewayCatalogKind } from "@/types/api";

const KIND_LABEL: Record<GatewayCatalogKind, string> = {
  cloud: "Cloud instance",
  customer: "Customer instance",
  section: "Section",
};

/**
 * A customer type's display name lives on its recipe; cloud instances and
 * sections keep theirs inside their opaque payload, where it may simply not
 * exist.
 */
function displayNameOf(entry: GatewayCatalogItem): string | null {
  if (entry.recipe?.displayName) return entry.recipe.displayName;
  const fromPayload = entry.payload?.display_name;
  return typeof fromPayload === "string" && fromPayload ? fromPayload : null;
}

/**
 * How many components a type actually ships: the always-included base plus
 * whatever its recipe picks. Counting only the recipe's picks would report
 * a type composed of nothing but the base as empty, which is precisely
 * backwards — that type ships the most widely shared components there are.
 *
 * ``null`` for anything without a recipe (cloud instances, sections, and
 * customer types still carrying a legacy payload): they aren't composed
 * from components at all, so no number would be truthful.
 */
function componentCountOf(
  entry: GatewayCatalogItem,
  alwaysIncluded: number,
): number | null {
  if (!entry.recipe) return null;
  return alwaysIncluded + entry.recipe.components.length;
}

// Backend invariant: instance entries (kind != "section") always carry a
// version. Anything that lands here without one is a DDB integrity bug —
// surface it loudly rather than emit /instances/<kind>/<model>//edit URLs
// that silently 404 (the very class of bug this rewrite eliminates).
function instanceRefOf(entry: GatewayCatalogItem): InstanceRef {
  if (entry.kind === "section") {
    throw new Error("instanceRefOf called on a section entry");
  }
  if (!entry.version) {
    throw new Error(
      `Catalog entry ${entry.kind}/${entry.model} is missing a version — DDB invariant violated.`,
    );
  }
  return { kind: entry.kind, model: entry.model, version: entry.version };
}

function editPath(entry: GatewayCatalogItem): string {
  if (entry.kind === "section") {
    return `/catalog/gateways/sections/${encodeURIComponent(entry.model)}/edit`;
  }
  const ref = instanceRefOf(entry);
  // Customer types are composed from catalog components, not edited as raw
  // JSON — route them to the dedicated composer instead of the generic
  // instance editor, which now only handles cloud instances.
  if (ref.kind === "customer") {
    return `/catalog/gateways/types/${encodeURIComponent(ref.model)}/${encodeURIComponent(ref.version)}/edit`;
  }
  return `/catalog/gateways/instances/${encodeURIComponent(ref.kind)}/${encodeURIComponent(ref.model)}/${encodeURIComponent(ref.version)}/edit`;
}

function clonePath(entry: GatewayCatalogItem): string {
  // Only instances are clonable — sections are singletons. The caller
  // gates this with ``entry.kind !== "section"`` so instanceRefOf is safe.
  const ref = instanceRefOf(entry);
  const ref_str = `${ref.kind}/${ref.model}/${ref.version}`;
  return `/catalog/gateways/new?cloneFrom=${encodeURIComponent(ref_str)}`;
}

function entryKey(entry: GatewayCatalogItem): string {
  return entry.kind === "section"
    ? `section#${entry.model}`
    : `${entry.kind}#${entry.model}#${entry.version ?? "<no-version>"}`;
}

function entryLabel(entry: GatewayCatalogItem): string {
  return entry.kind === "section"
    ? entry.model
    : `${entry.kind}/${entry.model}@${entry.version ?? "<no-version>"}`;
}

export function CatalogGatewaysPage() {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const qc = useQueryClient();

  const navigate = useNavigate();
  const [deleteTarget, setDeleteTarget] = useState<GatewayCatalogItem | null>(
    null,
  );

  const query = useQuery({
    queryKey: ["catalog", "gateways"],
    queryFn: () => listGateways(token),
    enabled: !!token,
  });

  // Two extra reads for the whole page, not two per row: the usage
  // aggregate sweeps the tenant registry once for every entry, and the
  // component list is the same cached query the Components page uses.
  const usageQuery = useQuery({
    queryKey: ["catalog", "gateway-usage"],
    queryFn: () => listGatewayUsage(token),
    enabled: !!token,
  });
  const componentsQuery = useQuery({
    queryKey: ["catalog", "components"],
    queryFn: () => listComponents(token),
    enabled: !!token,
  });

  const tenantCountByRef = new Map<string, number>();
  for (const row of usageQuery.data?.items ?? []) {
    tenantCountByRef.set(
      `${row.kind}#${row.model}#${row.version}`,
      row.tenantIds.length,
    );
  }
  const alwaysIncludedCount = (componentsQuery.data?.items ?? []).filter(
    (c) => c.inclusion === "always",
  ).length;

  const deleteMut = useMutation({
    mutationFn: async (entry: GatewayCatalogItem): Promise<void> => {
      if (entry.kind === "section") {
        await deleteSection(token, entry.model);
      } else {
        await deleteInstance(token, instanceRefOf(entry));
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["catalog", "gateways"] });
      qc.invalidateQueries({ queryKey: ["catalog", "gateway-usage"] });
      setDeleteTarget(null);
    },
  });

  const columns: TableColumn<GatewayCatalogItem>[] = [
    { key: "kind", header: "Kind", muted: true, cell: (entry) => KIND_LABEL[entry.kind] ?? entry.kind },
    {
      key: "name",
      header: "Name",
      cell: (entry) => (
        <Link to={editPath(entry)} className="font-mono text-brand-600 hover:underline">
          {entry.model}
        </Link>
      ),
    },
    { key: "display", header: "Display name", cell: (entry) => displayNameOf(entry) ?? "—" },
    { key: "version", header: "Version", mono: true, muted: true, cell: (entry) => entry.version ?? "—" },
    {
      key: "components",
      header: "Components",
      muted: true,
      cell: (entry) => componentCountOf(entry, alwaysIncludedCount) ?? "—",
    },
    {
      key: "usedBy",
      header: "Used by",
      muted: true,
      cell: (entry) => {
        if (entry.kind === "section") return "All tenants";
        if (!entry.version) return "—";
        const tenantCount = tenantCountByRef.get(`${entry.kind}#${entry.model}#${entry.version}`);
        return tenantCount === undefined ? "…" : `${tenantCount} tenant${tenantCount === 1 ? "" : "s"}`;
      },
    },
    { key: "updated", header: "Updated", mono: true, muted: true, cell: (entry) => formatDateTime(entry.updatedAt) },
  ];

  return (
    <Page>
      <PageHeader
        section={CATALOG}
        title="Gateway types"
        subtitle="Source-of-truth for the dataplane gateway types. Edits propagate to tenant SPAs within ~10 minutes."
        actions={
          <>
            <CatalogHealthBadge />
            <ButtonLink to="/catalog/gateways/types/new" variant="primary" icon={Plus}>
              New gateway type
            </ButtonLink>
            <ButtonLink
              to="/catalog/gateways/new"
              variant="secondary"
              icon={Plus}
              title="Cloud instance or global section"
            >
              New entry
            </ButtonLink>
          </>
        }
      />

      <RefreshFailed query={query} className="mb-4" />
      <Table
        label="Gateway types"
        columns={columns}
        rows={query.data?.items}
        rowKey={entryKey}
        error={loadFailure(query)}
        empty={
          <EmptyState
            title="The catalog is empty"
            action={
              <ButtonLink to="/catalog/gateways/types/new" variant="primary" icon={Plus}>
                New gateway type
              </ButtonLink>
            }
          >
            Create the first gateway type to start it.
          </EmptyState>
        }
        actions={{
          label: (entry) => `Actions for ${entryLabel(entry)}`,
          items: (entry) => (
            <>
              <MenuItem icon={Pencil} onClick={() => navigate(editPath(entry))}>
                Edit
              </MenuItem>
              {/* Cloud instances only: customer types are composed from
                  components, so there's no raw payload left to pre-fill a
                  clone with — the composer's model field is the closest
                  equivalent. */}
              {entry.kind === "cloud" && entry.version && (
                <MenuItem
                  icon={Copy}
                  title="Pre-fill a new entry with this payload to publish a revision"
                  onClick={() => navigate(clonePath(entry))}
                >
                  Clone
                </MenuItem>
              )}
              <MenuDivider />
              {/* The destructive gesture opens the confirmation, which shows the progress. */}
              <MenuItem danger icon={Trash2} disabled={deleteMut.isPending} onClick={() => setDeleteTarget(entry)}>
                Delete…
              </MenuItem>
            </>
          ),
        }}
      />

      {deleteTarget && (
        <ConfirmModal
          title="Delete catalog entry"
          confirmLabel="Delete"
          pendingLabel="Deleting…"
          danger
          pending={deleteMut.isPending}
          // Inside the modal, not on the page: the overlay hides the page, and a
          // refused delete (409) must say which tenants block it.
          error={deleteMut.error}
          errorView={(error) => (
            <DeleteError
              error={error}
              subject="This entry"
              noun="tenants"
              blockers={(details) => (Array.isArray(details?.tenantIds) ? (details.tenantIds as string[]) : [])}
              remedy={
                <>
                  Remove the reference in <span className="font-mono">allowed_*_instances</span> before retrying.
                </>
              }
            />
          )}
          onCancel={() => {
            deleteMut.reset();
            setDeleteTarget(null);
          }}
          onConfirm={() => deleteMut.mutate(deleteTarget)}
        >
          <p>
            Delete{" "}
            <code className="font-mono">{entryLabel(deleteTarget)}</code>? This
            action cannot be undone.
          </p>
        </ConfirmModal>
      )}
    </Page>
  );
}
